<template>
  <v-autocomplete
    v-model="shownSite"
    :items="items"
    :item-title="itemTitle"
    bg-color="white"
    color="primary"
    variant="outlined"
    density="compact"
    return-object
    @update:model-value="onInput"
    @blur="onBlur"
  >
    <template v-slot:label>
      <span class="text-primary text-caption font-weight-bold"> Site </span>
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import type { ISelectableSite } from '@/Core.UI.Domain/Components/types/siteAutocomplete.ts';

// The site selector every "bar" shows. Its one job beyond the shared look is
// to tell a real choice apart from the user clearing the text to type another
// site, which plain v-autocomplete reports as a `null` model.
const props = withDefaults(
  defineProps<{
    modelValue?: ISelectableSite | null;
    items: ISelectableSite[];
    itemTitle?: (item: ISelectableSite) => string;
  }>(),
  {
    itemTitle: (item: ISelectableSite) => item.name || '',
  }
);

// Never emits null: the parent's model always holds the site its page is on.
const emit = defineEmits<{
  'update:modelValue': [site: ISelectableSite];
}>();

// What the field shows. Unlike the parent's model it may be empty while the
// user is typing.
const shownSite = ref<ISelectableSite | null>(props.modelValue);

watch(
  () => props.modelValue,
  (site) => {
    shownSite.value = site;
  }
);

// Clearing the text (Backspace to the start, ⌘⌫, select-all + ⌫) makes
// v-autocomplete empty its model and emit null. That is the user about to
// type another site, not a choice: leave the field empty and tell the parent
// nothing, so the page stays on its site instead of jumping to the first one.
function onInput(site: ISelectableSite | null): void {
  if (!site) return;
  emit('update:modelValue', site);
}

// Leaving the field with nothing chosen puts the current site back, so the
// bar never shows an empty field over a page that belongs to a site.
function onBlur(): void {
  if (shownSite.value) return;
  shownSite.value = props.modelValue;
}
</script>
